import { useMemo, useState } from "react"

import H1 from "~/ui-rtk/components/ui/typography/H1"
import { DateRangePicker, ExportButton } from "~/ui-rtk/components/ui/controls"
import { AggregationType } from "~/ui-rtk/constants/charts"
import useDate from "~/ui-rtk/hooks/date"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import { convertTableDataToCSV, downloadCSV } from "~/ui-rtk/utils/csv-export"
import { formatNumber } from "~/ui-rtk/utils/format"

import useConnect, { selectedItemKey, type SubsequentFilters } from "./connect"
import SubsequentFiltersBar from "./components/SubsequentFilters"
import SubsequentTable from "./components/SubsequentTable"
import {
  approxMedianDaysToNext,
  avgDaysToNext,
  gapShare,
  repeatRate,
  type MedianGap,
} from "./utils/subsequent-rows"

const dayjs = useDate()

const DEFAULT_FILTERS: SubsequentFilters = {
  grain: "product",
  skuPrefixLength: 3,
  productId: null,
  skuPrefix: null,
  orderSequences: [],
}

const percent = (value: number | null) =>
  value === null ? "--" : `${(value * 100).toFixed(1)}%`

const days = (value: number | null) =>
  value === null ? "--" : `${formatNumber(value, { decimals: 0 })} days`

const medianGapLabel = (median: MedianGap) => {
  switch (median.kind) {
    case "value":
      return `~${formatNumber(median.days, { decimals: 0 })} days`
    case "beyond":
      return `over ${median.days} days`
    default:
      return "--"
  }
}

/** Wording for the denominator: "first orders" when only sequence 1 is selected. */
const originalOrdersLabel = (orderSequences: string[]) =>
  orderSequences.length === 1 && orderSequences[0] === "1"
    ? "first orders"
    : "original orders"

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1)

export default function SubsequentPurchasesPage() {
  // Customers need time to reorder, so the default window is a year of
  // original orders rather than the app-wide 30-day default.
  const defaultDates = useMemo<[Date, Date]>(
    () => [
      dayjs().subtract(12, "month").startOf("day").toDate(),
      dayjs().endOf("day").toDate(),
    ],
    [],
  )
  const { period, setPeriod } = usePeriod(
    AggregationType.DAILY,
    false,
    undefined,
    defaultDates,
  )
  const [filters, setFilters] = useState<SubsequentFilters>(DEFAULT_FILTERS)

  const range = useMemo(() => {
    const start =
      period?.[0] && isValidDate(period[0])
        ? dayjs(period[0])
        : dayjs(defaultDates[0])
    const end =
      period?.[1] && isValidDate(period[1])
        ? dayjs(period[1])
        : dayjs(defaultDates[1])
    return { start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) }
  }, [period, defaultDates])

  const { rows, denominators, itemOptions, isLoading, error, hasSelection } =
    useConnect({ filters, range })

  const originalLabel = originalOrdersLabel(filters.orderSequences)

  const handleExport = () => {
    const columnConfig = [
      {
        key: "title",
        header:
          filters.grain === "product"
            ? "Next-order product"
            : "Next-order SKU prefix",
        type: "text",
      },
      ...(filters.grain === "product"
        ? [{ key: "handle", header: "Handle", type: "text" }]
        : []),
      {
        key: "allOrdersSharePct",
        header: `${capitalize(originalLabel)} converting to this item (%)`,
        type: "number",
      },
      { key: "subsequentOrders", header: "Next orders", type: "number" },
      {
        key: "nextOrderSharePct",
        header: "Share of repeaters' next orders (%)",
        type: "number",
      },
      { key: "subsequentQuantity", header: "Units", type: "number" },
      {
        key: "subsequentRevenue",
        header: "Next-order revenue",
        type: "number",
      },
      {
        key: "avgDaysToNext",
        header: "Avg days to next order",
        type: "number",
      },
    ]
    const data = rows.map(row => ({
      title: row.title,
      handle: row.handle ?? "",
      nextOrderSharePct:
        row.nextOrderShare === null
          ? ""
          : (row.nextOrderShare * 100).toFixed(2),
      subsequentOrders: row.subsequentOrders,
      allOrdersSharePct:
        row.allOrdersShare === null
          ? ""
          : (row.allOrdersShare * 100).toFixed(2),
      subsequentQuantity: row.subsequentQuantity,
      subsequentRevenue: row.subsequentRevenue.toFixed(2),
      avgDaysToNext:
        row.avgDaysToNext === null ? "" : row.avgDaysToNext.toFixed(1),
    }))
    downloadCSV(
      convertTableDataToCSV(data, columnConfig),
      `subsequent-purchases-${filters.grain}-${selectedItemKey(filters)}-${range.start}-${range.end}.csv`,
    )
  }

  const handleChangePeriod = ({ start, end }: { start: Date; end: Date }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))
  }

  return (
    <div className="h-full px-4 py-2 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <H1>Subsequent Purchases</H1>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker
            start={dayjs(range.start).toDate()}
            end={dayjs(range.end).toDate()}
            comparisonEnabled={false}
            maxDate={new Date()}
            onChange={handleChangePeriod}
          />
          <ExportButton
            onExport={handleExport}
            disabled={isLoading || rows.length === 0}
          />
        </div>
      </div>

      <SubsequentFiltersBar
        filters={filters}
        onChange={setFilters}
        itemOptions={itemOptions}
      />

      {error ? (
        <div className="bg-system-blue-800 rounded-lg p-6 text-states-error">
          Failed to load subsequent purchases. Please try again or adjust the
          filters.
        </div>
      ) : (
        <>
          {hasSelection && !isLoading && denominators.originalOrders > 0 && (
            <div className="flex flex-wrap gap-6 text-sm text-gray-light-2">
              <span>
                <span className="text-white font-medium">
                  {formatNumber(denominators.originalOrders, { decimals: 0 })}
                </span>{" "}
                {originalLabel} contained this{" "}
                {filters.grain === "product" ? "product" : "prefix"}
              </span>
              <span>
                <span className="text-white font-medium">
                  {formatNumber(denominators.ordersWithNext, { decimals: 0 })}
                </span>{" "}
                were followed by another order (
                {percent(repeatRate(denominators))} repeat rate)
              </span>
              {denominators.timing && (
                <span>
                  <span className="text-white font-medium">
                    {medianGapLabel(approxMedianDaysToNext(denominators))}
                  </span>{" "}
                  median gap to the next order (avg{" "}
                  {days(avgDaysToNext(denominators))};{" "}
                  {percent(gapShare(denominators, 90))} within 90 days)
                </span>
              )}
            </div>
          )}
          <SubsequentTable
            grain={filters.grain}
            originalLabel={capitalize(originalLabel)}
            rows={rows}
            isLoading={isLoading}
            hasSelection={hasSelection}
          />
          <p className="text-xs text-gray-light-2">
            Rows are the items found in customers’ immediate next order after an
            order containing the selected{" "}
            {filters.grain === "product" ? "product" : "SKU prefix"}. “
            {capitalize(originalLabel)} converting to this item” divides the
            next orders that contained the item by every one of the{" "}
            {originalLabel} containing the selection, so customers who have not
            ordered again count as not converted. “Share of repeaters’ next
            orders” divides by only the {originalLabel} that were followed by
            another order; the two differ by the repeat rate shown above. The
            date range and “Original order” filter apply to the original order
            (sequences 1-4); next orders are counted whenever they happen. Gaps
            are calendar days from the original order to the customer’s next
            order; the median is interpolated from 30/60/90/180/365-day buckets.
            Recent original orders have had less time to be followed by another
            order, so a window that ends today understates conversion and repeat
            rate and shortens the gap figures. Only DTC e-commerce orders of
            identified customers are included.
          </p>
        </>
      )}
    </div>
  )
}
